import { ReactElement, useState } from 'react';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import styles from './OptionGroupTab.module.scss';
import { EmptyStateButton, Spinner, Typography } from '@weareopenr/component-library';
import { IMenuItem, IOptionGroup } from 'src/types/menu.types';
import ModifierCategory from '../ModifierCategory';
import { CreateOptionGroupModal } from '../CreateOptionGroupModal/CreateOptionGroupModal';
import { useItem } from 'src/hooks/useItem/useItem';
import PriceSchemeSelector from '../PriceSchemeSelector';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { ItemType } from '../../constants';
import { EditOptionGroupDetailsModal } from '../EditOptionGroupDetailsModal/EditOptionGroupDetailsModal';
import { EditOptionGroupPricingModal } from '../EditOptionGroupPricingModal';
import { EditOptionGroupExternalSystemsModal } from '../EditOptionGroupExternalSystemsModal/EditOptionGroupExternalSystemsModal';

const Content = ({ item }: { item: IMenuItem }): ReactElement => {
  const { selectedPriceSchemeId, setSelectedPriceSchemeId } = useSelectedPriceScheme();
  const [isOptionGroupModalOpen, setIsOptionGroupModalOpen] = useState<boolean>(false);
  const [isOptionGroupPricingModalOpen, setIsOptionGroupPricingModalOpen] = useState<boolean>(false);
  const [isOptionGroupExternalSystemModalOpen, setIsOptionGroupExternalSystemModalOpen] = useState<boolean>(false);
  const [optionGroupToEdit, setOptionGroupToEdit] = useState<IOptionGroup>();

  const isBundle = item?.type === ItemType.BUNDLE;

  const openOptionGroupModal = (optionGroupToEdit?: IOptionGroup): void => {
    setOptionGroupToEdit(optionGroupToEdit);
    setIsOptionGroupModalOpen(true);
  };

  const openOptionGroupPricingModal = (optionGroupToEdit?: IOptionGroup): void => {
    setOptionGroupToEdit(optionGroupToEdit);
    setIsOptionGroupPricingModalOpen(true);
  };

  const closeOptionGroupModal = () => {
    setIsOptionGroupModalOpen(false);
  };

  const closeOptionGroupPricingModal = () => {
    setIsOptionGroupPricingModalOpen(false);
  };
  const openOptionGroupExternalSystemsModal = (optionGroupToEdit?: IOptionGroup) => {
    setOptionGroupToEdit(optionGroupToEdit);
    setIsOptionGroupExternalSystemModalOpen(true);
  };
  const closeOptionGroupExternalSystemModal = () => {
    setIsOptionGroupExternalSystemModalOpen(false);
  };

  const ranges = item.ranges.map((range) => range.range_id);

  return (
    <>
      <CreateOptionGroupModal
        open={isOptionGroupModalOpen && !optionGroupToEdit}
        onClose={closeOptionGroupModal}
        itemId={item.itemId}
        itemType={item.type as ItemType.BUNDLE | ItemType.STANDARD}
        bundleRangeIds={ranges}
      />

      <EditOptionGroupDetailsModal
        item={item}
        isOpen={isOptionGroupModalOpen && !!optionGroupToEdit}
        optionGroup={optionGroupToEdit}
        onClose={closeOptionGroupModal}
      />

      <EditOptionGroupPricingModal
        isOpen={isOptionGroupPricingModalOpen}
        close={closeOptionGroupPricingModal}
        itemId={item.itemId}
        optionGroup={optionGroupToEdit}
      />
      <EditOptionGroupExternalSystemsModal
        isOpen={isOptionGroupExternalSystemModalOpen}
        closeModal={closeOptionGroupExternalSystemModal}
        optionGroup={optionGroupToEdit}
        itemId={item.itemId}
      />

      <div className={styles['container']}>
        <div className={styles['heading-container']}>
          <Typography size="xlarge" weight="semi-bold">
            {`${isBundle ? 'Bundle' : 'Modifier'} categories`}
          </Typography>
          <PriceSchemeSelector value={selectedPriceSchemeId} onChange={setSelectedPriceSchemeId} />
        </div>

        {item?.optionGroup.map((optionGroup, index) => (
          <ModifierCategory
            key={optionGroup.optionGroupId}
            index={index}
            optionGroup={optionGroup}
            currentItem={item}
            openOptionGroupModal={openOptionGroupModal}
            openOptionGroupPricingModal={openOptionGroupPricingModal}
            openOptionGroupExternalSystemsModal={openOptionGroupExternalSystemsModal}
          />
        ))}

        {(item.type === ItemType.STANDARD || item.type === ItemType.BUNDLE || item.type === ItemType.NON_FOOD) && (
          <EmptyStateButton
            label={`+ Create ${isBundle ? 'bundle' : 'modifier'}  category`}
            onClick={() => openOptionGroupModal()}
          />
        )}
      </div>
    </>
  );
};

const OptionGroupTab = ({ itemId }: { itemId: string }) => {
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const { data: item, isLoading } = useItem(itemId, selectedPriceSchemeId, { enabled: !!selectedPriceSchemeId });

  if (!selectedPriceSchemeId || isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }
  return <Content item={item} />;
};

export default OptionGroupTab;
